import { useState, useRef, useEffect, useLayoutEffect, useMemo, useDeferredValue } from "react";
import { createPortal } from "react-dom";
import { searchAirports, findAirport, normalizeQuery, type Airport } from "@/lib/airports";
import { Input } from "@/components/ui/input";
import { MapPin, X, Search, Globe } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface Props {
  value: string;
  onChange: (code: string) => void;
  label: string;
  placeholder?: string;
  id?: string;
  align?: "left" | "right";
  wrapperClassName?: string;
  /** Komunikat błędu — gdy ustawiony, podświetla pole na czerwono i renderuje treść pod inputem. */
  error?: string;
  /** Kod IATA do ukrycia z sugestii (np. żeby pole „Dokąd" nie pokazywało miasta wybranego w „Skąd"). */
  excludeCode?: string;
  /** Uporządkowana lista kodów IATA pokazywana, gdy input jest pusty (domyślne sugestie). */
  popularCodes?: string[];
  /** Gdy true, na górze listy pojawia się specjalna opcja „Gdziekolwiek" (inspiracja).
   *  Wpisanie fraz typu „wszędzie", „cały świat" itp. też ją wyzwala. Kliknięcie
   *  wywoła `onAnywhere()` zamiast `onChange()`. */
  anywhereOption?: boolean;
  onAnywhere?: () => void;
}

// Frazy uruchamiające opcję „Gdziekolwiek" (po normalizacji: lower + strip
// diakrytyków + alfanumeryczne). „wszędzie" → „wszedzie", „cały świat" →
// „calyswiat", itd.
// Frazy bazowe (już znormalizowane: lowercase, bez diakrytyków, bez spacji).
// Obsługujemy odmiany, warianty z/bez polskich znaków oraz literówki (edit
// distance ≤ 2 dla dłuższych słów).
const ANYWHERE_PHRASES = [
  "gdziekolwiek",
  "gdziekolwiekswiat",
  "wszedzie",
  "wszystko",
  "wszystkie",
  "calyswiat",
  "swiat",
  "naswiecie",
  "dowolne",
  "dowolnemiejsce",
  "obojetnie",
  "obojetniegdzie",
  "gdzies",
  "szukajwszedzie",
  "szukajwszystkiego",
  "pokazwszystko",
  "anywhere",
  "everywhere",
  "world",
  "anyplace",
  "anywhereintheworld",
];

// Klasyczny Levenshtein (iteracyjny, O(n*m)) — wystarczająco szybki dla
// krótkich stringów w pickerze.
function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const prev = new Array(b.length + 1);
  const curr = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= b.length; j++) prev[j] = curr[j];
  }
  return prev[b.length];
}

// Tolerancja literówek zależnie od długości frazy (krótkie słowa muszą być
// dokładne, dłuższe dopuszczają 1–2 błędy).
function typoTolerance(len: number): number {
  if (len <= 4) return 0;
  if (len <= 7) return 1;
  return 2;
}

function matchesAnywhere(q: string): boolean {
  const n = normalizeQuery(q);
  if (!n) return false;
  for (const p of ANYWHERE_PHRASES) {
    if (p === n) return true;
    // Częściowe wpisanie (prefix w obie strony) — od razu sugerujemy.
    if (p.startsWith(n) || n.startsWith(p)) return true;
    // Substring — np. user wpisał „polecę gdziekolwiek".
    if (n.includes(p) || p.includes(n)) return true;
    // Literówki — tylko gdy długości są zbliżone, by uniknąć fałszywych trafień
    // (np. nazw lotnisk).
    const tol = typoTolerance(Math.max(p.length, n.length));
    if (tol > 0 && Math.abs(p.length - n.length) <= tol) {
      if (editDistance(p, n) <= tol) return true;
    }
  }
  return false;
}

const PANEL_MAX_W = 448; // px (28rem)
const VIEWPORT_MARGIN = 16;
const INITIAL_COUNT = 10;

export function AirportPicker({ value, onChange, label, placeholder, id, align = "left", wrapperClassName, error, excludeCode, popularCodes, anywhereOption, onAnywhere }: Props) {
  const isMobile = useIsMobile();
  const selected = findAirport(value);
  // W polu input ZAWSZE skracamy etykietę. Metro → „Warszawa (wszystkie)",
  // kraj → „Polska (cały kraj)". Pełne formy „(wszystkie lotniska)" / „Cały
  // kraj" pojawiają się dopiero w rozwiniętej liście.
  const formatLabel = (a: Airport) =>
    a.isCountry
      ? `${a.city} (cały kraj)`
      : a.isMetro
        ? `${a.city} (wszystkie)`
        : `${a.city} (${a.code})`;
  const formatted = selected ? formatLabel(selected) : "";
  const [query, setQuery] = useState(formatted);
  const [open, setOpen] = useState(false);
  const [typing, setTyping] = useState(false);
  // Sync displayed label when `value` changes externally (np. swap, load z URL,
  // event `tl:set-from`). Bez tego picker nadal pokazywałby starą etykietę,
  // np. „Londyn (LON)” zamiast „Londyn (wszystkie)” po wymianie.
  useEffect(() => {
    if (!typing && !open) setQuery(formatted);
  }, [formatted, typing, open]);
  const [pos, setPos] = useState<{ left: number; top: number; width: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputWrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);
  // useDeferredValue: typing pozostaje natychmiastowe (INP-friendly na mobile),
  // a kosztowne filtrowanie 357 lotnisk + sort dzieje się w niższym priorytecie.
  const deferredQuery = useDeferredValue(query);
  const results = useMemo<Airport[]>(() => {
    const exclude = excludeCode?.toUpperCase();
    let next: Airport[];
    if (typing && deferredQuery.trim()) {
      next = searchAirports(deferredQuery);
    } else if (popularCodes && popularCodes.length > 0) {
      next = popularCodes
        .map((c) => findAirport(c))
        .filter((a): a is Airport => Boolean(a));
    } else {
      next = searchAirports("");
    }
    if (exclude) next = next.filter((a) => a.code !== exclude);
    const isDefaultEmpty = !(typing && deferredQuery.trim()) && !(popularCodes && popularCodes.length > 0);
    if (isDefaultEmpty) {
      next = [...next].sort(
        (a, b) =>
          Number(Boolean(b.isCountry || b.isMetro)) -
          Number(Boolean(a.isCountry || a.isMetro)),
      );
    }
    return next;
  }, [deferredQuery, typing, excludeCode, popularCodes]);
  const [showAll, setShowAll] = useState(false);
  const [highlight, setHighlight] = useState(0);
  // Reset stanu UI gdy zmienia się query (niezależnie od deferred wyniku).
  useEffect(() => {
    setShowAll(false);
    setHighlight(0);
  }, [query]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      const target = e.target as Node;
      if (
        !wrapRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setOpen(false);
        setTyping(false);
        setQuery(formatted);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [formatted]);

  // (Wcześniej mierzyliśmy szerokość, żeby decydować między „(wszystkie)"
  // a „(wszystkie lotniska)" — teraz w polu input zawsze pokazujemy krótką
  // wersję „(wszystkie)", więc pomiar jest niepotrzebny.)

  // Compute position clamped to viewport (desktop dropdown only)
  useLayoutEffect(() => {
    if (!open || isMobile) return;
    function update() {
      // Wyrównujemy panel do ZEWNĘTRZNEJ ramki pola (wrapRef), żeby zaczynał
      // się równo z napisem „SKĄD" / „DOKĄD" — a nie z polem inputu, które
      // jest dosunięte przez `pl-10` (miejsce na przycisk swap).
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vw = document.documentElement.clientWidth;
      const width = Math.min(PANEL_MAX_W, vw - VIEWPORT_MARGIN * 2);
      let left: number;
      if (align === "right") {
        left = r.right - width;
      } else {
        left = r.left;
      }
      // clamp horizontally so it never overflows viewport
      left = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - width - VIEWPORT_MARGIN));
      const top = r.bottom + 8;
      setPos({ left, top, width });
    }
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, align, results.length, isMobile]);

  // Mobile sheet: lock body scroll + autofocus search input
  useEffect(() => {
    if (!open || !isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => mobileInputRef.current?.focus(), 50);
    return () => {
      document.body.style.overflow = prev;
      clearTimeout(t);
    };
  }, [open, isMobile]);

  function pick(a: Airport) {
    onChange(a.code);
    setQuery(formatLabel(a));
    setOpen(false);
    setTyping(false);
  }

  function pickAnywhere() {
    if (!onAnywhere) return;
    // Analytics: rejestrujemy intencję „Gdziekolwiek" — keyword (jeśli user
    // coś wpisał) albo picker (kliknął opcję na liście domyślnej).
    const q = query.trim();
    void import("@/lib/analytics").then(({ trackAnywhereIntent }) => {
      trackAnywhereIntent({ source: q ? "keyword" : "picker", query: q || undefined });
    }).catch(() => {});
    setOpen(false);
    setTyping(false);
    setQuery(formatted);
    onAnywhere();
  }

  // „Gdziekolwiek" pokazujemy WYŁĄCZNIE gdy user wpisał frazę pasującą do
  // ANYWHERE_PHRASES — brak stałego przypięcia na liście domyślnej.
  const showAnywhereRow =
    !!anywhereOption && !!onAnywhere && !!query.trim() && matchesAnywhere(query);

  function handleFocus() {
    setOpen(true);
    // Wyczyść wpisaną wartość przy focusie, żeby user mógł od razu zacząć
    // pisać nowy kod/miasto bez konieczności kasowania starej wartości.
    // Jeśli zamknie picker bez wyboru, click-outside przywróci `formatted`.
    setQuery("");
    setTyping(true);
  }

  function visibleResults() {
    return showAll ? results : results.slice(0, INITIAL_COUNT);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
        setTyping(false);
        setQuery(formatted);
        inputRef.current?.blur();
      }
      return;
    }
    const items = visibleResults();
    if (!items.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlight((h) => (h + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlight((h) => (h - 1 + items.length) % items.length);
    } else if (e.key === "Home") {
      e.preventDefault();
      setHighlight(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setHighlight(items.length - 1);
    } else if (e.key === "Enter") {
      if (open && items[highlight]) {
        e.preventDefault();
        pick(items[highlight]);
      }
    }
  }

  const errorId = error && id ? `${id}-error` : undefined;
  return (
    <div
      ref={wrapRef}
      className={cn(
        "relative rounded-lg border bg-secondary/60 dark:bg-black/30 px-3 py-2 shadow-sm transition-colors",
        error
          ? "border-destructive bg-destructive/5 focus-within:border-destructive"
          : "border-border dark:border-white/20 focus-within:border-primary focus-within:bg-secondary dark:focus-within:bg-black/40",
        wrapperClassName,
      )}
    >
      <label htmlFor={id} className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </label>
      <div ref={inputWrapRef} className="relative">
        <MapPin className={cn("pointer-events-none absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2", error ? "text-destructive" : "text-primary")} />
        <Input
          id={id}
          ref={inputRef}
          value={query}
          placeholder={placeholder}
          onFocus={handleFocus}
          onMouseDown={(e) => {
            // Drugi klik na otwartym pickerze zamyka listę.
            if (open) {
              e.preventDefault();
              setOpen(false);
              setTyping(false);
              setQuery(formatted);
              inputRef.current?.blur();
            }
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setTyping(true);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          // Na mobile inline input jest tylko triggerem otwierającym sheet —
          // dzięki readOnly iOS Safari nie podpina klawiatury do tego pola
          // (klawiatura zostawała na inline inpucie zamiast przeskoczyć do
          // pola wyszukiwarki w sheecie, więc user pisał „w próżnię”).
          readOnly={isMobile}
          inputMode={isMobile ? "none" : undefined}
          className="h-9 border-0 bg-transparent pl-6 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 lg:h-8 lg:p-0 lg:pl-6"
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls={id ? `${id}-listbox` : undefined}
          aria-activedescendant={open && visibleResults()[highlight] ? `${id ?? "ap"}-opt-${visibleResults()[highlight].code}` : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
        />
      </div>
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-xs font-medium text-destructive">
          {error}
        </p>
      )}
      {open && (results.length > 0 || showAnywhereRow) && !isMobile && pos && (
        <div
          ref={panelRef}
          style={{ position: "fixed", left: pos.left, top: pos.top, width: pos.width }}
          className="z-[60] flex max-h-[70vh] flex-col overflow-hidden rounded-lg border border-border bg-popover shadow-elevated"
        >
          <ul className="flex-1 overflow-y-auto py-2" role="listbox" id={id ? `${id}-listbox` : undefined}>
            {showAnywhereRow && (
              <li role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={pickAnywhere}
                  className="flex w-full items-center justify-between gap-4 border-b border-border/40 bg-primary/[0.06] px-5 py-3 text-left transition-colors hover:bg-primary/[0.12]"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <Globe className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-base font-semibold text-primary">Gdziekolwiek</div>
                      <div className="truncate text-sm text-muted-foreground">Najtańsze kierunki na świecie — inspiracja</div>
                    </div>
                  </div>
                </button>
              </li>
            )}
            {(showAll ? results : results.slice(0, INITIAL_COUNT)).map((a, i) => (
              <li key={a.code} role="presentation">
                <button
                  type="button"
                  id={`${id ?? "ap"}-opt-${a.code}`}
                  role="option"
                  aria-selected={i === highlight}
                  ref={(el) => {
                    if (i === highlight && el) el.scrollIntoView({ block: "nearest" });
                  }}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => pick(a)}
                  className={cn(
                    "flex w-full items-center justify-between gap-4 px-5 py-3 text-left transition-colors hover:bg-accent/30",
                    a.code === value && "bg-accent/20",
                    (a.isMetro || a.isCountry) && "bg-primary/[0.04]",
                    i === highlight && "bg-accent/40",
                  )}
                >
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-base font-semibold">
                        {a.city}
                        {a.isCountry ? (
                          <span className="ml-1 font-normal">(cały kraj)</span>
                        ) : a.isMetro ? (
                          <span className="ml-1 font-normal">(wszystkie lotniska)</span>
                        ) : null}
                      </div>
                      <div className="truncate text-sm text-muted-foreground">
                        {a.isCountry
                          ? `${a.members?.length ?? 0} lotnisk w całym kraju`
                          : a.isMetro
                            ? a.country
                            : `${a.name} · ${a.country}`}
                      </div>
                    </div>
                  </div>
                  {!a.isMetro && !a.isCountry && (
                    <span className="shrink-0 rounded-md bg-primary/15 px-2.5 py-1 font-mono text-xs font-bold text-primary">
                      {a.code}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
          {!showAll && results.length > INITIAL_COUNT && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="border-t border-border px-5 py-3 text-center text-sm font-semibold text-primary hover:bg-accent/20"
            >
              Pokaż więcej popularnych ({results.length - INITIAL_COUNT})
            </button>
          )}
        </div>
      )}
      {open && isMobile && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 z-[100] flex flex-col bg-background">
          <div ref={panelRef} className="flex h-full flex-col">
            <div className="flex items-center gap-2 border-b border-border px-3 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  ref={mobileInputRef}
                  value={query}
                  placeholder={placeholder ?? "Miasto, kraj lub kod"}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setTyping(true);
                  }}
                  onKeyDown={handleKeyDown}
                  className="h-11 pl-9 text-base"
                  autoComplete="off"
                  autoFocus
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setTyping(false);
                  setQuery(formatted);
                }}
                aria-label="Zamknij"
                className="flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground hover:bg-accent/30"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {label}
            </div>
            <ul className="flex-1 overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)]">
              {showAnywhereRow && (
                <li>
                  <button
                    type="button"
                    onClick={pickAnywhere}
                    className="flex w-full items-center justify-between gap-4 border-b border-border/40 bg-primary/[0.06] px-5 py-4 text-left transition-colors active:bg-primary/[0.15]"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <Globe className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-base font-semibold text-primary">Gdziekolwiek</div>
                        <div className="truncate text-sm text-muted-foreground">Najtańsze kierunki na świecie</div>
                      </div>
                    </div>
                  </button>
                </li>
              )}
              {results.map((a) => (
                <li key={a.code}>
                  <button
                    type="button"
                    onClick={() => pick(a)}
                    className={cn(
                      "flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors active:bg-accent/40",
                      a.code === value && "bg-accent/20",
                      (a.isMetro || a.isCountry) && "bg-primary/[0.04]"
                    )}
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-base font-semibold">
                          {a.city}
                          {a.isCountry ? (
                            <span className="ml-1 font-normal">(cały kraj)</span>
                          ) : a.isMetro ? (
                            <span className="ml-1 font-normal">(wszystkie lotniska)</span>
                          ) : null}
                        </div>
                        <div className="truncate text-sm text-muted-foreground">
                          {a.isCountry
                            ? `${a.members?.length ?? 0} lotnisk w całym kraju`
                            : a.isMetro
                              ? a.country
                              : `${a.name} · ${a.country}`}
                        </div>
                      </div>
                    </div>
                    {!a.isMetro && !a.isCountry && (
                      <span className="shrink-0 rounded-md bg-primary/15 px-2.5 py-1 font-mono text-xs font-bold text-primary">
                        {a.code}
                      </span>
                    )}
                  </button>
                </li>
              ))}
              {results.length === 0 && (
                <li className="px-5 py-6 text-center text-sm text-muted-foreground">Brak wyników</li>
              )}
            </ul>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
